import React, { FC, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { Connect, PropsFromRedux } from './SearchModalContainer';

import {
  ModalFullScreen,
  ModalFullScreenContent,
} from '../../../../../../components/modal';
import { ModalSearchFullScreenHeader } from '../../../../../../components/modal/components/modal-header/full-screen/ModalSearchFullScreenHeader';
import { SearchBar } from '../../../../../../search/components/search-bar/SearchBar';
import { Container, Row } from '../../../../../../components/grid/Grid';
import { SearchSection } from '../../../../../../search/components/search-section/SearchSection';

import { placeholderMap } from '../../utils/constants';

import styles from './search_modal.module.scss';

type Props = {
  isOpen: boolean;
  onRequestClose: (query: string) => void;
  restrictedIds: string[];
  title: string;
} & PropsFromRedux;

const RawSearchModal: FC<Props> = ({
  isOpen,
  restrictedIds,
  title,
  query,
  type,
  onRequestClose,
  resetSearchQuery,
  replaySearch,
  switchSearchFeatures,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const parentContainerRef = useRef<HTMLDivElement>(null);
  const [persistedQuery, setPersistedQuery] = useState('');
  const queryParam = persistedQuery ? `q=${persistedQuery}` : '';

  useEffect(() => {
    setPersistedQuery(query);
  }, []);

  const resetSearchQueryOnRequestClose = () => {
    onRequestClose(queryParam);
    resetSearchQuery(`${pathname}?${queryParam}`);
  };

  useEffect(() => {
    switchSearchFeatures();
  }, [isOpen, switchSearchFeatures]);

  useEffect(() => {
    return history.listen(() => {
      if (history.action === 'POP') {
        if (history.location.search.includes('restrict_to_law_article_ids=')) {
          replaySearch(history.location.search, 'browser_navigation');
        } else {
          // Make sure original query is persisted.
          resetSearchQuery(`${pathname}${queryParam}`);
        }
      }
    });
  }, [history]);

  useEffect(() => {
    if (history.location.search.includes('restrict_to_law_article_ids=')) {
      replaySearch(search, 'original_query');
    }
  }, []);

  if (!isOpen) return null;

  return (
    <ModalFullScreen
      isOpen={isOpen}
      onRequestClose={resetSearchQueryOnRequestClose}
    >
      <ModalSearchFullScreenHeader
        title={title}
        onClose={resetSearchQueryOnRequestClose}
        searchBarComponent={
          <SearchBar
            activateScrollToTop={false}
            restrictedDocIds={restrictedIds}
            allowEmptyQuery
            placeholder={
              placeholderMap[type] &&
              intl.formatMessage({
                id: placeholderMap[type],
              })
            }
          />
        }
      />
      <ModalFullScreenContent>
        <div className={styles.scrollParentContainer} ref={parentContainerRef}>
          <Container className={styles.container}>
            <Row>
              <SearchSection parentContainerRef={parentContainerRef.current} />
            </Row>
          </Container>
        </div>
      </ModalFullScreenContent>
    </ModalFullScreen>
  );
};

export const SearchModal = Connect(RawSearchModal);
